import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventsAPI } from '@/services';

const HERO_IMG =
  'https://images.unsplash.com/photo-1576267423048-15c0040fec78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbGxhYm9yYXRpb24lMjB0ZWFtd29ya3xlbnwwfHx8fDE3NjUzOTY4Nzl8MA&ixlib=rb-4.1.0&q=85';
const SOLUTIONS_IMG =
  'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80';
const INFO_IMG =
  'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&q=80';
const IMPACT_A =
  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80';
const IMPACT_B =
  'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800&q=80';

/** Message d’alerte (maintenance, incident). Vide = pas de barre d’alerte. */
const publicAlertMessage = (process.env.REACT_APP_ENTREA_PUBLIC_ALERT || '').trim();

const ChevronMore = ({ className = '' }) => (
  <span className={`inline-block font-semibold ${className}`} aria-hidden>
    {' >'}
  </span>
);

const ArrowLink = ({ href, children, to }) => {
  const className =
    'group inline-flex items-center gap-0.5 text-sm font-semibold text-entrea-royal hover:text-entrea-navy transition';
  if (to) {
    return (
      <Link to={to} className={className}>
        {children}
        <ChevronMore className="transition group-hover:translate-x-0.5" />
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {children}
      <ChevronMore className="transition group-hover:translate-x-0.5" />
    </a>
  );
};

const LogoMark = ({ variant = 'light' }) => {
  const dark = variant === 'dark';
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-3xl bg-gradient-to-br from-entrea-ink to-entrea-royal shadow-lg">
        <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
        </svg>
      </div>
      <div className="flex min-w-0 flex-col leading-tight">
        <span className={`text-lg font-bold tracking-tight ${dark ? 'text-white' : 'text-gray-900'}`}>
          Entrea
        </span>
        <span
          className={`text-[10px] font-semibold uppercase tracking-[0.12em] sm:text-[11px] ${
            dark ? 'text-entrea-sky/90' : 'text-gray-500'
          }`}
        >
          Uruganda Platform
        </span>
      </div>
    </div>
  );
};

/** Barre utilitaire (réf. UNFCU) — liens secondaires ; les alertes sont en dessous. */
const UtilityBar = () => (
  <div className="border-b border-gray-200 bg-gray-100">
    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-xs text-gray-600 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        <a href="#apropos" className="font-medium hover:text-entrea-ink">
          À propos
        </a>
        <a href="#footer-contact" className="font-medium hover:text-entrea-ink">
          Contact
        </a>
        <a href="#ressources" className="font-medium hover:text-entrea-ink">
          Ressources
        </a>
        <Link to="/register" className="font-semibold text-entrea-ink hover:text-entrea-navy md:hidden">
          Rejoindre
        </Link>
      </div>
      <span className="hidden text-gray-500 sm:inline">Suite Uruganda Platform</span>
    </div>
  </div>
);

const PublicAlertBar = () => {
  if (!publicAlertMessage) return null;

  return (
    <div
      role="alert"
      className="border-b border-amber-300/60 bg-gradient-to-r from-amber-50 via-amber-50 to-amber-100/90"
    >
      <div className="mx-auto flex max-w-7xl items-start gap-3 px-4 py-2.5 sm:items-center sm:px-6 lg:px-8">
        <span
          className="mt-0.5 inline-flex h-6 shrink-0 items-center rounded-md bg-amber-200/80 px-1.5 text-[10px] font-bold uppercase tracking-wide text-amber-950 sm:mt-0"
          aria-hidden
        >
          Alerte
        </span>
        <p className="flex-1 text-left text-sm font-medium leading-snug text-amber-950">{publicAlertMessage}</p>
      </div>
    </div>
  );
};

/**
 * Accueil public Entrea — structure type institution financière (UNFCU),
 * couleurs alignées Login / Register (palette Entrea institutionnelle).
 */
const PublicEntreaLanding = () => {
  const [publicEvents, setPublicEvents] = useState([]);

  useEffect(() => {
    const loadPublicEvents = async () => {
      try {
        const res = await eventsAPI.listPublic({ limit: 6, skip: 0 });
        const rows = Array.isArray(res.data) ? res.data : (res.data?.events || []);
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const startOfTodayTs = startOfToday.getTime();

        const normalized = rows
          .map((row) => {
            const candidate = row?.planned_date || row?.start_date || row?.scheduled_at || row?.created_at;
            const parsed = new Date(candidate);
            const ts = parsed.getTime();
            return {
              ...row,
              _sortTs: Number.isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts,
            };
          })
          .filter((row) => row._sortTs >= startOfTodayTs)
          .sort((a, b) => a._sortTs - b._sortTs)
          .slice(0, 6)
          .map(({ _sortTs, ...row }) => row);

        setPublicEvents(normalized);
      } catch {
        setPublicEvents([]);
      }
    };
    loadPublicEvents();
  }, []);

  const formatPublicDate = (value) => {
    if (!value) return 'Date à confirmer';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'Date à confirmer';
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <UtilityBar />
      <PublicAlertBar />

      {/* Navigation principale : logo | menu centré | recherche + Rejoindre (outline) + Se connecter (plein) */}
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:gap-6 lg:px-8 lg:py-4">
          <Link
            to="/"
            className="shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-entrea-royal/40 focus-visible:ring-offset-2"
          >
            <LogoMark />
          </Link>

          <nav
            className="hidden items-center justify-center gap-6 lg:flex lg:flex-1 xl:gap-10"
            aria-label="Navigation principale"
          >
            <a
              href="#actualites"
              className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Actualités
              <svg className="h-3.5 w-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
            <a
              href="#solutions"
              className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Solutions
              <svg className="h-3.5 w-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
            <a href="#fonctionnalites" className="text-sm font-medium text-gray-700 hover:text-gray-900">
              Fonctionnalités
            </a>
            <a href="#impact" className="text-sm font-medium text-gray-700 hover:text-gray-900">
              Engagement
            </a>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="#actualites"
              className="hidden rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-entrea-royal md:inline-flex"
              aria-label="Aller aux actualités"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </a>
            <Link
              to="/register"
              className="hidden rounded-xl border-entrea-royal bg-white px-4 py-2 text-sm font-semibold text-entrea-royal shadow-sm transition hover:bg-entrea-panel sm:inline-flex"
            >
              Rejoindre
            </Link>
            <Link
              to="/login"
              className="rounded-xl bg-entrea-royal px-3 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-entrea-navy hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-entrea-royal/40 focus-visible:ring-offset-2 active:scale-[0.98] sm:px-5"
            >
              Se connecter
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero — fond gris clair, CTA bleu plein + chevron (réf. UNFCU) */}
        <section id="apropos" className="scroll-mt-28 border-b border-gray-200 bg-gray-50">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 md:gap-16 md:py-24 lg:px-8">
            <div className="max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-entrea-royal">Uruganda Platform</p>
              <p className="mt-2 text-sm font-semibold text-gray-900">Entrea · Caisse de solidarité</p>
              <h1 className="mt-4 text-4xl font-bold leading-[1.12] tracking-tight text-gray-900 sm:text-5xl">
                Donnez vie à votre projet associatif
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-gray-600">
                Membres, cotisations et transparence : tout ce dont votre organisation a besoin, au sein de la suite{' '}
                <span className="font-semibold text-gray-900">Uruganda Platform</span>.
              </p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
                <Link
                  to="/register"
                  className="inline-flex w-fit items-center justify-center rounded-lg bg-entrea-royal px-8 py-3.5 text-base font-semibold text-white shadow-md transition hover:bg-entrea-navy hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-entrea-royal/40 focus-visible:ring-offset-2"
                >
                  Découvrir nos solutions
                  <ChevronMore />
                </Link>
                <Link
                  to="/login"
                  className="text-base font-semibold text-entrea-royal underline-offset-4 hover:underline"
                >
                  Accès membre — connexion
                </Link>
              </div>
            </div>
            <div className="flex justify-center md:justify-end">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-entrea-sky/25 via-entrea-panel/35 to-entrea-surface/25 blur-2xl" aria-hidden />
                <div className="relative mx-auto aspect-square max-w-[340px] overflow-hidden rounded-full border-[6px] border-white shadow-2xl ring-1 ring-gray-200/80">
                  <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-br from-entrea-royal/20 via-transparent to-entrea-sky/15" aria-hidden />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Actualités — trois cartes + hint carousel (cercle vert) */}
        <section id="actualites" className="scroll-mt-28 border-b border-gray-100 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
                Actualités & informations
              </h2>
              <div
                className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600 shadow-sm md:flex"
                aria-hidden
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            <div className="grid gap-8 md:grid-cols-3">
              {[
                { tag: 'Produit', title: 'Indicateurs et tableaux de bord pour piloter votre association', to: '#fonctionnalites' },
                { tag: 'Conformité', title: 'Cotisations traçables et exports pour vos rapports', to: '#solutions' },
                { tag: 'Communauté', title: 'Calendrier partagé et inscriptions aux événements', to: '#fonctionnalites' },
              ].map((card) => (
                <article
                  key={card.title}
                  className="flex flex-col rounded-xl border border-gray-200/90 bg-white p-8 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.08)] transition hover:shadow-[0_8px_30px_-6px_rgba(79,70,229,0.12)]"
                >
                  <p className="text-xs font-bold uppercase tracking-wider text-entrea-royal">{card.tag}</p>
                  <h3 className="mt-4 flex-1 text-lg font-bold leading-snug text-gray-900">{card.title}</h3>
                  <div className="mt-6">
                    <ArrowLink href={card.to}>Lire l&apos;article</ArrowLink>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="scroll-mt-28 border-b border-gray-100 bg-entrea-panel/30 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
                Événements publics
              </h2>
              <Link to="/login" className="text-sm font-semibold text-entrea-ink hover:text-entrea-navy">
                Accès membre &gt;
              </Link>
            </div>
            {publicEvents.length === 0 ? (
              <div className="rounded-2xl border border-gray-200 bg-white p-8 text-sm text-gray-600">
                Aucun événement public en ce moment.
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {publicEvents.map((ev) => (
                  <article key={String(ev.id)} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-entrea-royal">
                      {ev.is_free ? 'Gratuit' : 'Inscription'}
                    </p>
                    <h3 className="mt-2 text-lg font-bold text-gray-900 line-clamp-2">{ev.title || 'Événement'}</h3>
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2">{ev.description || 'Consultez le détail pour plus d’informations.'}</p>
                    <p className="mt-4 text-sm font-medium text-gray-700">{formatPublicDate(ev.planned_date || ev.start_date || ev.scheduled_at)}</p>
                    <div className="mt-4">
                      <Link to={`/events/public/${ev.id}`} className="inline-flex items-center text-sm font-semibold text-entrea-royal hover:text-entrea-navy">
                        Voir l’événement <ChevronMore />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Solutions globales — image + liste chevrons */}
        <section id="solutions" className="scroll-mt-28 border-b border-gray-100 bg-gray-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-12 max-w-3xl text-3xl font-bold leading-tight text-gray-900 md:text-4xl">
              Des solutions pensées pour les organisations et leurs membres
            </h2>
            <div className="grid items-stretch gap-12 lg:grid-cols-2 lg:gap-16">
              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
                <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[420px]">
                  <img src={SOLUTIONS_IMG} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-indigo-900/30 via-transparent to-pink-600/20" aria-hidden />
                </div>
              </div>
              <ul className="flex flex-col justify-center divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white shadow-sm">
                {[
                  { label: 'Espace membres et rôles', href: '#fonctionnalites' },
                  { label: 'Cotisations et demandes d’approbation', href: '#fonctionnalites' },
                  { label: 'Suivi financier et rapports', href: '#fonctionnalites' },
                  { label: 'Calendrier et événements', href: '#fonctionnalites' },
                  { label: 'Documents et procès-verbaux', href: '#fonctionnalites' },
                  { label: 'Organisation — paramètres et modules', href: '/register-organization', to: true },
                ].map((row) => (
                  <li key={row.label}>
                    {row.to ? (
                      <Link
                        to={row.href}
                        className="flex items-center justify-between gap-4 px-6 py-4 text-[15px] font-medium text-entrea-ink transition hover:bg-gray-50"
                      >
                        <span className="text-gray-900">{row.label}</span>
                        <span className="text-indigo-500" aria-hidden>
                          &gt;
                        </span>
                      </Link>
                    ) : (
                      <a
                        href={row.href}
                        className="flex items-center justify-between gap-4 px-6 py-4 text-[15px] font-medium text-entrea-ink transition hover:bg-gray-50"
                      >
                        <span className="text-gray-900">{row.label}</span>
                        <span className="text-indigo-500" aria-hidden>
                          &gt;
                        </span>
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Grille aide + 2x2 (réf. “Make the most…”) */}
        <section id="fonctionnalites" className="scroll-mt-28 border-b border-gray-100 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-14 lg:grid-cols-12 lg:items-start lg:gap-16">
              <div className="lg:col-span-4">
                <h2 className="text-3xl font-bold leading-tight text-gray-900 md:text-4xl">
                  Profitez de tout ce qu&apos;offre Entrea
                </h2>
                <p className="mt-5 text-lg text-gray-600">
                  Modules activables selon l&apos;abonnement de votre organisation sur Uruganda Platform.
                </p>
                <Link
                  to="/register"
                  className="mt-8 inline-flex items-center rounded-lg border-entrea-royal bg-white px-6 py-3 text-sm font-semibold text-entrea-royal shadow-sm transition hover:bg-entrea-panel"
                >
                  Visiter le centre d&apos;aide
                  <ChevronMore />
                </Link>
              </div>
              <div id="ressources" className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
                {[
                  { tag: 'Membres', title: 'Annuaire et permissions par rôle', desc: 'Adhérents, bureau et bénévoles au même endroit.' },
                  { tag: 'Finances', title: 'Transactions et catégories', desc: 'Visibilité pour le bureau et les rapports.' },
                  { tag: 'Calendrier', title: 'Événements et inscriptions', desc: 'Du public aux réunions internes.' },
                  { tag: 'Documents', title: 'PV, statuts et fichiers utiles', desc: 'Moins de pièces jointes perdues.' },
                ].map((item) => (
                  <article
                    key={item.title}
                    className="flex flex-col rounded-xl border border-gray-200 bg-gray-50/80 p-6 shadow-sm transition hover:border-entrea-panel hover:bg-white"
                  >
                    <p className="text-xs font-bold uppercase tracking-wider text-entrea-royal">{item.tag}</p>
                    <h3 className="mt-3 text-base font-bold text-gray-900">{item.title}</h3>
                    <p className="mt-2 flex-1 text-sm text-gray-600">{item.desc}</p>
                    <div className="mt-4">
                      <ArrowLink to="/register">En savoir plus</ArrowLink>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Savoir / connaissance — image + carte blanche */}
        <section className="border-b border-gray-100 bg-gray-50 py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-xl">
              <img src={INFO_IMG} alt="" className="aspect-[3/4] w-full object-cover object-top sm:aspect-[4/5]" />
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.1)] sm:p-10 md:p-12">
              <h2 className="text-3xl font-bold leading-tight text-gray-900 md:text-4xl">
                Renforcez vos pratiques. Agissez avec clarté.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-gray-600">
                Entrea centralise chiffres, échéances et responsabilités pour les équipes et les instances — sans
                multiplier les fichiers éparpillés.
              </p>
              <div className="mt-8">
                <ArrowLink to="/register">Lire nos articles</ArrowLink>
              </div>
            </div>
          </div>
        </section>

        {/* Impact — deux cartes larges */}
        <section id="impact" className="scroll-mt-28 border-b border-gray-100 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
              Engagement & transparence
            </h2>
            <div className="grid gap-10 md:grid-cols-2">
              <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md">
                <div className="relative aspect-[16/10]">
                  <img src={IMPACT_A} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent" aria-hidden />
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-bold text-gray-900">Une gouvernance lisible</h3>
                  <p className="mt-3 text-gray-600">
                    Rapports et décisions utiles pour l&apos;assemblée et les membres.
                  </p>
                  <div className="mt-6">
                    <ArrowLink to="/register">Voir notre impact</ArrowLink>
                  </div>
                </div>
              </article>
              <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md">
                <div className="relative aspect-[16/10]">
                  <img src={IMPACT_B} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent" aria-hidden />
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-bold text-gray-900">Moins d&apos;outils, plus d&apos;action</h3>
                  <p className="mt-3 text-gray-600">Une base commune pour l&apos;équipe et les bénévoles.</p>
                  <div className="mt-6">
                    <ArrowLink to="/register">Voir notre impact</ArrowLink>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* CTA pré-footer — bandeau teal plein (réf. UNFCU) */}
        <section className="bg-[#0d5c55] py-16 md:py-20">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
            <h2 className="text-3xl font-bold leading-tight text-white md:text-4xl">
              Rejoignez les organisations qui s&apos;appuient sur Entrea
            </h2>
            <p className="mt-5 text-lg text-teal-100/95">
              Créez votre compte ou enregistrez votre structure sur Uruganda Platform.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-5 sm:flex-row">
              <Link
                to="/register"
                className="inline-flex min-w-[220px] items-center justify-center rounded-lg border-2 border-white bg-transparent px-10 py-3.5 text-base font-semibold text-white transition hover:bg-white/10"
              >
                Rejoindre Entrea
                <ChevronMore className="text-white" />
              </Link>
              <Link
                to="/login"
                className="text-base font-semibold text-white underline-offset-4 hover:underline"
              >
                Se connecter
              </Link>
            </div>
            <p className="mt-8 text-sm text-teal-200/90">
              <Link to="/register-organization" className="font-semibold text-white underline decoration-white/40 hover:decoration-white">
                Représentez une organisation ?
              </Link>{' '}
              — enregistrement ou rattachement.
            </p>
          </div>
        </section>
      </main>

      {/* Pied de page — bandeau liens + colonnes + barre légale */}
      <footer className="bg-[#1e293b] text-gray-300">
        <div className="border-b border-white/10">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-8 gap-y-2 px-4 py-4 text-sm sm:px-6 lg:px-8">
            <a href="#apropos" className="hover:text-white">
              À propos
            </a>
            <a href="#footer-contact" className="hover:text-white">
              Nous contacter
            </a>
            <a href="#ressources" className="hover:text-white">
              Ressources
            </a>
            <span className="text-gray-500">Adhésion &amp; éligibilité</span>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-12 border-b border-white/10 pb-12 md:grid-cols-2 lg:grid-cols-4">
            <div id="footer-contact">
              <LogoMark variant="dark" />
              <p className="mt-5 text-sm leading-relaxed">
                <strong className="text-white">Entrea</strong>, produit{' '}
                <strong className="text-white">Uruganda Platform</strong> — gestion associative, membres et finances.
              </p>
              <p className="mt-4 text-xs text-gray-500">Support depuis votre espace après connexion.</p>
              <p className="mt-6 text-xs text-gray-500">© {new Date().getFullYear()} Uruganda Platform · Entrea</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Offre Entrea</p>
              <ul className="mt-4 space-y-3 text-sm">
                <li>
                  <a href="#solutions" className="hover:text-white">
                    Solutions
                  </a>
                </li>
                <li>
                  <a href="#fonctionnalites" className="hover:text-white">
                    Fonctionnalités
                  </a>
                </li>
                <li>
                  <a href="#impact" className="hover:text-white">
                    Engagement
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Accès</p>
              <ul className="mt-4 space-y-3 text-sm">
                <li>
                  <Link to="/login" className="hover:text-white">
                    Se connecter
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="hover:text-white">
                    Créer un compte
                  </Link>
                </li>
                <li>
                  <Link to="/register-organization" className="hover:text-white">
                    Organisations
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Légal</p>
              <p className="mt-4 text-xs leading-relaxed text-gray-500">
                En utilisant Entrea, vous acceptez les conditions d&apos;utilisation applicables à votre organisation
                sur Uruganda Platform.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center justify-between gap-6 pt-8 md:flex-row">
            <div className="flex flex-wrap items-center gap-6 text-xs text-gray-500">
              <span className="rounded border border-gray-600 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                Égalité &amp; accès
              </span>
              <span className="rounded border border-gray-600 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                Données protégées
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-xs">
              <a href="#apropos" className="hover:text-white">
                Politique de confidentialité
              </a>
              <a href="#apropos" className="hover:text-white">
                Conditions d&apos;utilisation
              </a>
            </div>
            <div className="flex items-center gap-4 text-gray-500" aria-label="Réseaux sociaux">
              <span className="h-8 w-8 rounded-full border border-gray-600" title="LinkedIn" />
              <span className="h-8 w-8 rounded-full border border-gray-600" title="YouTube" />
            </div>
          </div>
        </div>

        <div className="border-t border-black/40 bg-black/40 py-4">
          <p className="text-center text-xs text-gray-600">
            Entrea — produit Uruganda Platform. Outils pour agir ensemble.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicEntreaLanding;
